import React, { useMemo, useState } from "react";
import { ExternalLink, Film, Play } from "lucide-react";
import { formatDate, formatDateTime, formatMoney } from "../../shared/utils/format";
import { useSearchParams } from "react-router-dom";
import { useSearchParamUpdater } from "../../shared/hooks/useSearchParamUpdater";
import { useUserSection } from "./useUserSection";
import { PostPreviewModal } from "./PostPreviewModal";
import {
  Column,
  DataTable,
  LoadState,
  Muted,
  Pagination,
  PlatformChips,
  SectionCard,
  StageBadge,
  StatusBadge,
  stageLabel,
} from "./ui";
import type {
  ContentStage,
  BillingEventRow,
  BillingExtra,
  BillingSubscriptionRow,
  BlogRow,
  CampaignRow,
  ConnectionRow,
  CreditSummary,
  EmailRow,
  LedgerRow,
  Paged,
  PaymentRow,
  PostRow,
  TopicRow,
  WeekRow,
} from "./types";

const PAGE_SIZE = 20;

interface TabProps {
  userId: number;
  clientId?: number;
}

const BrandCell: React.FC<{ name: string | null }> = ({ name }) => (
  <span className="whitespace-nowrap">{name || "—"}</span>
);

const Clamp: React.FC<{ text: string | null; lines?: 2 | 3 }> = ({ text, lines = 2 }) =>
  text ? (
    <span className={`block max-w-md ${lines === 2 ? "line-clamp-2" : "line-clamp-3"}`} title={text}>
      {text}
    </span>
  ) : (
    <span className="text-gray-400">—</span>
  );

const ErrorText: React.FC<{ text: string | null }> = ({ text }) =>
  text ? <span className="mt-1 block max-w-md text-xs text-red-600 line-clamp-3" title={text}>{text}</span> : null;

const StatusFilter: React.FC<{
  value: string;
  options: string[];
  onChange: (value: string) => void;
  labelFor?: (option: string) => string;
}> = ({ value, options, onChange, labelFor }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700"
  >
    <option value="">All statuses</option>
    {options.map((o) => (
      <option key={o} value={o}>
        {labelFor ? labelFor(o) : o.replaceAll("_", " ")}
      </option>
    ))}
  </select>
);

// Paginated table for one section; page and status filter come from the URL (?page=&status=).
function PagedSection<T extends { id: number | string }, Extra = object>({
  userId,
  clientId,
  section,
  title,
  columns,
  emptyText,
  statusOptions,
  statusLabel,
  summary,
}: Readonly<{
  userId: number;
  clientId?: number;
  section: string;
  title: string;
  columns: Column<T>[];
  emptyText: string;
  statusOptions?: string[];
  statusLabel?: (option: string) => string;
  summary?: (data: Paged<T> & Extra) => React.ReactNode;
}>) {
  const [searchParams] = useSearchParams();
  const updateSearchParams = useSearchParamUpdater();
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "", 10) || 1);
  const statusParam = searchParams.get("status") ?? "";
  const status = statusOptions?.includes(statusParam) ? statusParam : "";
  const setPage = (next: number) => updateSearchParams({ page: next > 1 ? next : null });
  const { data, loading, error, reload } = useUserSection<Paged<T> & Extra>(userId, section, {
    clientId,
    status: status || undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  return (
    <SectionCard
      title={title}
      action={statusOptions && (
        <StatusFilter
          value={status}
          options={statusOptions}
          labelFor={statusLabel}
          onChange={(value) => updateSearchParams({ status: value, page: null })}
        />
      )}
    >
      <LoadState loading={loading} error={error} onRetry={reload} />
      {!loading && !error && data && (
        <>
          {summary?.(data)}
          <DataTable columns={columns} rows={data.items} emptyText={emptyText} />
          <Pagination page={data.page} totalPages={data.totalPages} totalCount={data.totalCount} onChange={setPage} />
        </>
      )}
    </SectionCard>
  );
}

type OpenPost = (postId: number) => void;

function PostThumb({ post, onOpen }: Readonly<{ post: PostRow; onOpen: OpenPost }>) {
  if (!post.thumbnailUrl && !post.hasVideo) return <span className="text-gray-400">—</span>;
  return (
    <button
      type="button"
      aria-label={`Preview post ${post.id}`}
      onClick={() => onOpen(post.id)}
      className="relative block h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 hover:ring-2 hover:ring-violet-400"
    >
      {post.thumbnailUrl ? (
        <img src={post.thumbnailUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <Film className="m-auto h-5 w-5 text-gray-400" />
      )}
      {post.hasVideo && <Play className="absolute inset-0 m-auto h-5 w-5 fill-white text-white drop-shadow" />}
      {post.imageCount > 1 && (
        <span className="absolute bottom-0.5 right-0.5 rounded bg-black/60 px-1 text-[10px] font-medium text-white">
          {post.imageCount}
        </span>
      )}
    </button>
  );
}

const postColumns = (onOpen: OpenPost): Column<PostRow>[] => [
  { header: "Media", render: (p) => <PostThumb post={p} onOpen={onOpen} /> },
  { header: "Brand", render: (p) => <BrandCell name={p.businessName} /> },
  {
    header: "Post",
    render: (p) => (
      <>
        <button type="button" onClick={() => onOpen(p.id)} className="text-left hover:text-violet-700">
          <Clamp text={p.topic ?? p.caption} />
        </button>
        <Muted>
          {p.contentType ?? p.media_type ?? "—"} · {(p.platforms ?? []).join(", ") || "no platforms"}
        </Muted>
      </>
    ),
  },
  {
    header: "Stage",
    render: (p) => (
      <>
        <StageBadge
          stage={p.stage}
          detail={`record: ${p.status ?? "—"} · generation: ${p.generationStatus} · publishing: ${p.publishStatus}`}
        />
        <PlatformChips results={p.publishResults} />
        <ErrorText text={p.stageReason} />
      </>
    ),
  },
  { header: "Scheduled", render: (p) => formatDateTime(p.scheduledAt), className: "whitespace-nowrap" },
  { header: "Published", render: (p) => formatDateTime(p.publishedAt), className: "whitespace-nowrap" },
  { header: "Created", render: (p) => formatDate(p.createdAt), className: "whitespace-nowrap" },
];

const topicColumns = (onOpen: OpenPost): Column<TopicRow>[] => [
  { header: "Brand", render: (t) => <BrandCell name={t.businessName} /> },
  {
    header: "Topic",
    render: (t) => (
      <>
        <Clamp text={t.topic} />
        {t.headline && <Muted>{t.headline}</Muted>}
      </>
    ),
  },
  {
    header: "Type",
    render: (t) => (
      <>
        {t.contentType ?? "—"}
        {t.autopilotGenerated && <Muted> · autopilot</Muted>}
      </>
    ),
  },
  {
    header: "Stage",
    render: (t) => (
      <>
        <span className="flex items-center gap-1.5">
          <StageBadge
            stage={t.stage}
            detail={`topic: ${t.status ?? "—"} · post: ${t.generationStatus ?? "none"} · publishing: ${t.publishStatus ?? "none"}`}
          />
          {t.postId !== null && (
            <button type="button" onClick={() => onOpen(t.postId as number)} className="text-xs text-violet-600 hover:underline">
              View
            </button>
          )}
        </span>
        <PlatformChips results={t.publishResults} />
        <ErrorText text={t.stageReason} />
      </>
    ),
  },
  { header: "Post date", render: (t) => formatDate(t.postDate), className: "whitespace-nowrap" },
];

const campaignColumns: Column<CampaignRow>[] = [
  { header: "Brand", render: (c) => <BrandCell name={c.businessName} /> },
  {
    header: "Campaign",
    render: (c) => (
      <>
        <span className="font-medium">{c.name}</span>
        <Muted>
          <span className="block capitalize">
            {c.platform} · {c.channelType} · {c.objective}
          </span>
        </Muted>
        <ErrorText text={c.publications.lastError} />
      </>
    ),
  },
  { header: "Status", render: (c) => <StatusBadge status={c.status} /> },
  {
    header: "Budget",
    render: (c) => (
      <span className="whitespace-nowrap">
        {formatMoney(Number(c.budgetAmount), c.currency)} <Muted>/ {c.budgetType.toLowerCase()}</Muted>
      </span>
    ),
  },
  { header: "Creatives", render: (c) => c.creatives },
  {
    header: "Spend",
    render: (c) => <span className="whitespace-nowrap">{formatMoney(c.performance.spend, c.currency)}</span>,
  },
  {
    header: "Clicks / Conv.",
    render: (c) => `${c.performance.clicks} / ${c.performance.conversions}`,
    className: "whitespace-nowrap",
  },
  {
    header: "Ads",
    render: (c) => {
      const entries = Object.entries(c.publications.byStatus);
      return entries.length === 0 ? (
        <Muted>not published</Muted>
      ) : (
        <span className="flex flex-wrap gap-1">
          {entries.map(([s, n]) => (
            <span key={s} className="flex items-center gap-1">
              <StatusBadge status={s} /> {n}
            </span>
          ))}
        </span>
      );
    },
  },
  {
    header: "Dates",
    render: (c) => `${formatDate(c.startDate)} – ${c.endDate ? formatDate(c.endDate) : "open"}`,
    className: "whitespace-nowrap",
  },
];

const addDays = (isoDate: string, days: number) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
};

/** Post counts per stage for one week. */
const StageCounts: React.FC<{ counts: Record<string, number>; empty: string }> = ({ counts, empty }) => {
  const entries = Object.entries(counts);
  if (entries.length === 0) return <Muted>{empty}</Muted>;
  return (
    <span className="flex flex-wrap gap-1.5">
      {entries.map(([stage, n]) => (
        <span key={stage} className="flex items-center gap-1">
          <StageBadge stage={stage as ContentStage} /> {n}
        </span>
      ))}
    </span>
  );
};

const weekColumns: Column<WeekRow>[] = [
  {
    header: "Week",
    render: (w) => `${formatDate(addDays(w.weekStartDate, 0))} – ${formatDate(addDays(w.weekStartDate, 6))}`,
    className: "whitespace-nowrap",
  },
  { header: "Brand", render: (w) => <BrandCell name={w.businessName} /> },
  { header: "Topics", render: (w) => w.topics },
  { header: "Posts", render: (w) => <StageCounts counts={w.posts} empty="none generated" /> },
  {
    header: "Generation jobs",
    render: (w) =>
      w.jobs.length === 0 ? (
        <Muted>no job record</Muted>
      ) : (
        <span className="flex flex-col gap-1">
          {w.jobs.map((j) => (
            <span key={j.jobType}>
              <span className="flex items-center gap-1.5">
                <span className="text-xs text-gray-600">{j.jobType.replaceAll("_", " ")}</span>
                <StatusBadge status={j.status} />
                {j.attempt > 1 && <Muted>attempt {j.attempt}</Muted>}
              </span>
              <ErrorText text={j.errorMessage} />
            </span>
          ))}
        </span>
      ),
  },
];

const blogColumns: Column<BlogRow>[] = [
  { header: "Brand", render: (b) => <BrandCell name={b.businessName} /> },
  {
    header: "Blog",
    render: (b) => (
      <>
        <Clamp text={b.title} />
        {b.primaryKeyword && <Muted>Keyword: {b.primaryKeyword}</Muted>}
        <ErrorText text={b.wpErrorMessage ?? b.generationError} />
      </>
    ),
  },
  { header: "Status", render: (b) => <StatusBadge status={b.status} /> },
  { header: "Quality", render: (b) => b.qualityScore ?? "—" },
  {
    header: "WordPress",
    render: (b) =>
      b.wpUrl ? (
        <a href={b.wpUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-violet-600 hover:underline">
          View <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <span className="text-gray-400">—</span>
      ),
  },
  { header: "Scheduled", render: (b) => formatDateTime(b.scheduledFor), className: "whitespace-nowrap" },
  { header: "Published", render: (b) => formatDateTime(b.publishedAt), className: "whitespace-nowrap" },
];

const ledgerReason = (e: LedgerRow): string | null => {
  const reason = e.metadata?.reason ?? e.metadata?.note;
  return typeof reason === "string" ? reason.replaceAll("_", " ") : null;
};

const ledgerColumns: Column<LedgerRow>[] = [
  { header: "When", render: (e) => formatDateTime(e.createdAt), className: "whitespace-nowrap" },
  { header: "Type", render: (e) => <StatusBadge status={e.entryType} /> },
  {
    header: "Amount",
    render: (e) => {
      const credit = e.entryType === "GRANT" || e.entryType === "ADJUSTMENT";
      return <span className={`font-medium ${credit ? "text-emerald-700" : "text-red-600"}`}>{credit ? "+" : "−"}{e.amount}</span>;
    },
  },
  { header: "Bucket", render: (e) => e.bucket.replaceAll("_", " ").toLowerCase() },
  { header: "Source", render: (e) => e.source.replaceAll("_", " ").toLowerCase() },
  { header: "Note", render: (e) => ledgerReason(e) ?? <span className="text-gray-400">—</span> },
  { header: "Expires", render: (e) => formatDate(e.expiresAt), className: "whitespace-nowrap" },
];

const emailColumns: Column<EmailRow>[] = [
  { header: "When", render: (e) => formatDateTime(e.sentAt ?? e.failedAt ?? e.createdAt), className: "whitespace-nowrap" },
  { header: "Email", render: (e) => e.notificationType.replaceAll("_", " ").toLowerCase() },
  { header: "Brand", render: (e) => <BrandCell name={e.businessName} /> },
  { header: "Status", render: (e) => <StatusBadge status={e.status} /> },
  { header: "Reason", render: (e) => e.skipReason?.replaceAll("_", " ") ?? <span className="text-gray-400">—</span> },
];

export const PostsTab: React.FC<TabProps> = (props) => {
  const [previewId, setPreviewId] = useState<number | null>(null);
  const columns = useMemo(() => postColumns(setPreviewId), []);
  return (
    <>
      <PagedSection<PostRow>
        {...props}
        section="posts"
        title="Posts"
        columns={columns}
        emptyText="No posts yet."
        statusOptions={["creating", "ready", "scheduled", "published", "failed", "removed"]}
        statusLabel={stageLabel}
      />
      {previewId !== null && (
        <PostPreviewModal userId={props.userId} postId={previewId} onClose={() => setPreviewId(null)} />
      )}
    </>
  );
};

export const TopicsTab: React.FC<TabProps> = (props) => {
  const [previewId, setPreviewId] = useState<number | null>(null);
  const columns = useMemo(() => topicColumns(setPreviewId), []);
  return (
    <>
      <PagedSection<TopicRow>
        {...props}
        section="topics"
        title="Topics"
        columns={columns}
        emptyText="No topics generated yet."
        statusOptions={["topic_only", "creating", "ready", "scheduled", "published", "failed", "removed"]}
        statusLabel={stageLabel}
      />
      {previewId !== null && (
        <PostPreviewModal userId={props.userId} postId={previewId} onClose={() => setPreviewId(null)} />
      )}
    </>
  );
};

export const WeeksTab: React.FC<TabProps> = (props) => (
  <PagedSection<WeekRow>
    {...props}
    section="weeks"
    title="Content weeks"
    columns={weekColumns}
    emptyText="No content weeks yet."
  />
);

export const CampaignsTab: React.FC<TabProps> = (props) => (
  <PagedSection<CampaignRow>
    {...props}
    section="campaigns"
    title="Ad campaigns"
    columns={campaignColumns}
    emptyText="No ad campaigns yet."
    statusOptions={["DRAFT", "SCHEDULED", "ACTIVE", "PAUSED", "COMPLETED", "CANCELLED", "ARCHIVED"]}
  />
);

export const BlogsTab: React.FC<TabProps> = (props) => (
  <PagedSection<BlogRow>
    {...props}
    section="blogs"
    title="Blogs"
    columns={blogColumns}
    emptyText="No blogs yet."
    statusOptions={["draft", "scheduled", "publishing", "published", "failed"]}
  />
);

const renderCreditSummary = ({ summary }: { summary: CreditSummary }) => (
  <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
    {[
      { label: "Available", value: summary.available ?? "Unavailable" },
      { label: "Granted", value: summary.granted },
      { label: "Spent", value: summary.spent },
      { label: "Expired", value: summary.expired },
    ].map((s) => (
      <div key={s.label} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
        <p className="text-lg font-semibold text-gray-900">{s.value}</p>
        <p className="text-xs text-gray-500">{s.label}</p>
      </div>
    ))}
  </div>
);

export const CreditsTab: React.FC<TabProps> = ({ userId }) => (
  <PagedSection<LedgerRow, { summary: CreditSummary }>
    userId={userId}
    section="credits"
    title="Credit history"
    columns={ledgerColumns}
    emptyText="No credit activity."
    summary={renderCreditSummary}
  />
);

export const EmailsTab: React.FC<TabProps> = ({ userId }) => (
  <PagedSection<EmailRow>
    userId={userId}
    section="emails"
    title="Lifecycle emails"
    columns={emailColumns}
    emptyText="No lifecycle emails yet."
  />
);

export const AccountsTab: React.FC<TabProps> = ({ userId, clientId }) => {
  const { data, loading, error, reload } = useUserSection<{ items: ConnectionRow[] }>(userId, "accounts", { clientId });

  return (
    <SectionCard title="Connected accounts">
      <LoadState loading={loading} error={error} onRetry={reload} />
      {!loading && !error && data?.items.length === 0 && (
        <p className="py-12 text-center text-sm text-gray-400">No connected accounts.</p>
      )}
      {!loading && !error && data && data.items.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {data.items.map((c) => (
            <li key={c.id} className="py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-gray-900">
                  <span className="capitalize">{c.provider.replaceAll("_", " ")}</span>
                  <span className="ml-2 font-normal text-gray-500">{c.displayName ?? c.externalUserId ?? ""}</span>
                  <span className="ml-2 text-xs font-normal text-gray-400">{c.businessName ?? ""}</span>
                </span>
                <span className="flex items-center gap-2">
                  <StatusBadge status={c.status} />
                  {c.lastHeartbeatStatus && <StatusBadge status={`health ${c.lastHeartbeatStatus}`} />}
                </span>
              </div>
              <Muted>
                Connected {formatDate(c.createdAt)}
                {c.lastHeartbeatAt && ` · checked ${formatDateTime(c.lastHeartbeatAt)}`}
                {c.expiresAt && ` · token expires ${formatDate(c.expiresAt)}`}
              </Muted>
              <ErrorText text={c.lastHeartbeatError} />
              {(c.publishing_accounts.length > 0 || c.ad_accounts.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.publishing_accounts.map((a) => (
                    <span key={`p-${a.id}`} className="rounded-md border border-gray-200 px-2 py-0.5 text-xs text-gray-700" title={a.lastSyncError ?? undefined}>
                      {a.kind.toLowerCase()}: {a.name ?? a.externalId}
                      {a.isPrimary && " ★"} · {a.status.toLowerCase()}
                    </span>
                  ))}
                  {c.ad_accounts.map((a) => (
                    <span key={`a-${a.id}`} className="rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs text-violet-700">
                      ad account: {a.name ?? a.externalId} {a.currency ? `(${a.currency})` : ""} · {a.status.toLowerCase()}
                      {a.conversionTrackingVerifiedAt ? " · conversions verified" : ""}
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
};

const label = (value: string | null | undefined) => (value ? value.replaceAll("_", " ").toLowerCase() : null);
const money = (amount: number | null, currency: string | null) => {
  if (amount === null) return null;
  const code = currency?.toUpperCase() ?? null;
  return formatMoney(amount, code);
};

const journeyColumns: Column<BillingEventRow>[] = [
  { header: "When", render: (e) => formatDateTime(e.occurredAt), className: "whitespace-nowrap" },
  { header: "Event", render: (e) => <StatusBadge status={e.type} /> },
  {
    header: "Plan",
    render: (e) => {
      const plan = e.fromPlan && e.toPlan && e.fromPlan !== e.toPlan ? `${e.fromPlan} → ${e.toPlan}` : e.toPlan ?? e.fromPlan;
      return plan ?? <span className="text-gray-400">—</span>;
    },
  },
  { header: "Amount", render: (e) => money(e.amount, e.currency) ?? <span className="text-gray-400">—</span> },
  { header: "By", render: (e) => label(e.source) },
];

const subscriptionColumns: Column<BillingSubscriptionRow>[] = [
  { header: "Created", render: (s) => formatDate(s.createdAt), className: "whitespace-nowrap" },
  {
    header: "Plan",
    render: (s) =>
      s.prices ? `${s.prices.plans.name} · ${money(s.prices.amount, s.prices.currency)}` : <span className="text-gray-400">—</span>,
  },
  { header: "Status", render: (s) => <StatusBadge status={s.status} /> },
  { header: "Provider", render: (s) => s.provider },
  {
    header: "Trial",
    render: (s) =>
      s.trialEndAt ? (
        <span className="whitespace-nowrap">
          {formatDate(s.trialStartAt)} → {formatDate(s.trialEndAt)}
          {s.trialEndedReason && <span className="block text-xs text-gray-500">{label(s.trialEndedReason)}</span>}
        </span>
      ) : (
        <span className="text-gray-400">—</span>
      ),
  },
  { header: "Then", render: (s) => s.nextPlan ?? <span className="text-gray-400">—</span> },
  { header: "Last payment error", render: (s) => <Clamp text={s.lastPaymentError} /> },
];

const paymentColumns: Column<PaymentRow>[] = [
  { header: "When", render: (p) => formatDateTime(p.paidAt ?? p.createdAt), className: "whitespace-nowrap" },
  { header: "Type", render: (p) => label(p.type) },
  { header: "Status", render: (p) => <StatusBadge status={p.status} /> },
  { header: "Amount", render: (p) => money(p.amount, p.currency) },
  { header: "Method", render: (p) => p.method ?? <span className="text-gray-400">—</span> },
  { header: "Payment id", render: (p) => <span className="font-mono text-xs">{p.providerPaymentId}</span> },
  { header: "Reason", render: (p) => <Clamp text={p.failureReason} /> },
];

const renderBillingSummary = ({ subscriptions, payments, referral }: BillingExtra) => (
  <div className="mb-6 space-y-5">
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
      {referral ? (
        <>
          Referral reward: <span className="font-medium">+{referral.amount} credits</span>
          {referral.referralCode && <> via code <span className="font-mono">{referral.referralCode}</span></>} on{" "}
          {formatDate(referral.grantedAt)}
          {referral.expiresAt && <>, expires {formatDate(referral.expiresAt)}</>}
        </>
      ) : (
        <Muted>No referral reward.</Muted>
      )}
    </div>
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Subscriptions</p>
      <DataTable columns={subscriptionColumns} rows={subscriptions} emptyText="No subscriptions." />
    </div>
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Payments</p>
      <DataTable columns={paymentColumns} rows={payments} emptyText="No payments." />
    </div>
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Journey</p>
  </div>
);

export const BillingTab: React.FC<TabProps> = ({ userId }) => (
  <PagedSection<BillingEventRow, BillingExtra>
    userId={userId}
    section="billing"
    title="Billing history"
    columns={journeyColumns}
    emptyText="No subscription activity yet."
    summary={renderBillingSummary}
  />
);
